import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { usePermission } from '../../hooks';
import HaventPermission from '../../assets/HaventPermission';
import usePageWidth from '../shared/usePageWidth';
import styles from './viewStyles.module.css';

const ComplexTopicsDisciplinesView = ({
  id_ctp,
  viewData,
  viewIsLoading,
  viewErrorStatus,
  getComplexTopicsDisciplineView,
  clearComplexTopicsDisciplineView,
  history,
  location,
}) => {
  usePageWidth('pageWide');

  const { readRight } = usePermission('Справочники', 'Комплексный тем.план (КТП) по дисциплинам');
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (readRight && id_ctp) {
      getComplexTopicsDisciplineView(id_ctp);
    }
    return () => clearComplexTopicsDisciplineView();
  }, [id_ctp, readRight]);

  // возврат к списку с теми же условиями отбора и страницей
  const handleBack = () => {
    const listState = location?.state?.complexDisciplinesList;
    if (listState) history.replace({
      pathname: '/complex_topics_disciplines',
      state: { complexDisciplinesList: listState },
    });
    else history.goBack();
  };

  const renderValue = (value) => (value || value === 0)
    ? value
    : <span className={styles.dash}>—</span>;

  if (!readRight) return <HaventPermission />;

  if (viewErrorStatus) {
    const message = viewErrorStatus === 403 ? 'Нет доступа к данному КТП дисциплины'
      : viewErrorStatus === 404 ? 'КТП дисциплины не найден'
        : 'Сервер вернул ошибку';

    return (
      <div className={styles.page}>
        <div className={styles.error}>
          <div className={styles.errorTitle}>Не удалось открыть КТП дисциплины</div>
          {message}
          <div>
            <button className={styles.backBtn} onClick={handleBack}>
              Вернуться к списку
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (viewIsLoading || !viewData) {
    return (
      <div className={styles.page}>
        <div className={styles.loader}>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  const main = viewData.main || {};
  const studyScheme = viewData.study_scheme || [];
  // итоговой суммы часов в ответе нет — считается по выведенным строкам
  const totalHours = studyScheme.reduce((sum, row) => sum + (row.hours || 0), 0);

  // три столбца по четыре строки: раскладка задаётся порядком полей в сетке
  const fields = [
    { label: 'Название КТП', value: main.name },
    { label: 'Учебный год', value: main.school_year },
    { label: 'Учебный период', value: main.study_period },
    { label: 'Курс', value: main.education_year },
    { label: 'Семестр', value: main.education_term },
    { label: 'Учебный план', value: main.curriculum },
    { label: 'Кафедра', value: main.cathedra },
    { label: 'Дисциплина', value: main.discipline },
    { label: 'Учебная программа', value: main.work_program },
    { label: 'Тематический план', value: main.theme_plan },
    { label: 'Факультет', value: main.faculty },
    { label: 'Группа', value: main.groupname },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={handleBack}>
          ← Назад
        </button>
        <h1 className={styles.title}>КТП дисциплины</h1>
      </div>

      <div className={styles.tabList}>
        <button
          className={tabIndex === 0 ? styles.tabActive : styles.tab}
          onClick={() => setTabIndex(0)}
        >
          Главная
        </button>
        <button
          className={tabIndex === 1 ? styles.tabActive : styles.tab}
          onClick={() => setTabIndex(1)}
        >
          Схема изучения (СЛС)
        </button>
      </div>

      {tabIndex === 0 && (
        <div className={styles.fieldsGrid}>
          {/* значение — текстом, а не disabled input: длинная строка переносится
              внутри плашки и читается целиком, плашка растёт по высоте */}
          {fields.map(({ label, value }) => (
            <div key={label}>
              <span className={styles.fieldLabel}>{label}</span>
              <div className={styles.fieldValue}>{renderValue(value)}</div>
            </div>
          ))}
        </div>
      )}

      {tabIndex === 1 && (
        studyScheme.length === 0 ? (
          <div className={styles.noData}>Данные отсутствуют</div>
        ) : (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.colSt}>Код</th>
                    <th>Наименование темы</th>
                    <th className={styles.colSt}>Вид занятия</th>
                    <th className={styles.colSt}>Группа</th>
                    <th className={styles.colHh}>Часы</th>
                    <th className={styles.colHh}>Дл.</th>
                    <th className={styles.colSt}>Преподаватель</th>
                    <th className={styles.colSt}>Аудитория</th>
                  </tr>
                </thead>
                <tbody>
                  {/* порядок строк задан сервером — на клиенте не пересортировывается */}
                  {studyScheme.map((row, i) => (
                    <tr key={row.id_ctpsi != null ? `${row.id_ctpsi}-${i}` : i}>
                      <td className={styles.colSt}>{renderValue(row.o_title_index)}</td>
                      <td className={styles.left}>{renderValue(row.o_title)}</td>
                      <td className={styles.colSt}>{renderValue(row.event_tools)}</td>
                      <td className={`${styles.colSt} ${styles.mono}`}>{renderValue(row.groupname)}</td>
                      <td className={styles.colHh}>{renderValue(row.hours)}</td>
                      <td className={styles.colHh}>{renderValue(row.hours_per_lesson)}</td>
                      <td className={styles.colSt}>{renderValue(row.teachers)}</td>
                      <td className={styles.colSt}>{renderValue(row.rooms)}</td>
                    </tr>
                  ))}
                  <tr className={styles.totalRow}>
                    <td></td>
                    <td className={styles.left}>Всего часов</td>
                    <td></td>
                    <td></td>
                    <td className={styles.colHh}>{totalHours}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className={styles.tableFooter}>Всего тем: <b>{studyScheme.length}</b></div>
          </>
        )
      )}
    </div>
  );
};

export default ComplexTopicsDisciplinesView;
